import * as React from 'react'
import {
  Stage,
  Layer,
  Image as KImage,
  Circle,
  Line,
  Wedge,
  Text,
  Group,
} from 'react-konva'
import type Konva from 'konva'
import {
  ChartScanActivity,
  ChartScanDisc,
  ChartScanDistanceData,
  ActivityType,
} from '@/lib/api/company/chartScan'

import {
  ActivityBoundaryHandles,
  toChartIso,
  MINUTES_PER_DAY,
  isoToMinute,
  minuteToAngleRad,
  parseChartIso,
} from '@/modules/imports/components/TachographChartCanvas'

type Props = {
  imageUrl: string
  disc: ChartScanDisc
  activities: ChartScanActivity[]
  onActivitiesChange: (next: ChartScanActivity[], sameDrag: boolean) => void
  hoveredActivityId?: number | null
  selectedActivityIds?: Set<number>
  onHoverActivity?: (id: number | null, fromCanvas: boolean) => void
  onSelectActivity?: (ids: number[], shift: boolean, ctrl: boolean) => void

  onContextMenuActivity?: (
    activity: ChartScanActivity | null,
    screenXY: { x: number; y: number },
    minute: number,
  ) => void
  startBoundaryMinute?: number | null
  endBoundaryMinute?: number | null
  noonShiftMinutes?: number

  outOfDayGrey?: boolean
  onChangeStartBoundary?: (minute: number) => void
  onChangeEndBoundary?: (minute: number) => void
  onChangeNoonShift?: (minute: number) => void
  onCursorTimeChange?: (minute: number | null) => void

  onClickEmptyArea?: (minute: number) => void
  // WPF adds in adding mode by clicking an activity, so wedge clicks go to onClickEmptyArea too.
  addingMode?: boolean
 
  distanceData?: ChartScanDistanceData | null
  showDistance?: boolean

  onAddDistancePoint?: (
    kind: 'start' | 'end',
    point: { x: number; y: number },
  ) => void
  onRemoveDistancePoint?: (kind: 'start' | 'end', index: number) => void
  beginCountryCode?: string
  endCountryCode?: string
  vehicleChanges?: { id: number; minute: number; label?: string }[]
  onMoveVehicleChange?: (id: number, minute: number) => void
  onContextMenuVehicleChange?: (id: number, screenXY: { x: number; y: number }) => void
  countryChanges?: { minute: number; code: string }[]
  width?: number
  height?: number
  zoom?: number
  onZoomChange?: (zoom: number) => void

  compactView?: boolean
}


const CANONICAL_SIZE = 800
const CANONICAL_CENTER = 400
const CANONICAL_OUTER_RADIUS = 400

const CANONICAL_BORDER = 40
const IMAGE_SCALE_RATIO =
  (CANONICAL_OUTER_RADIUS - CANONICAL_BORDER) / CANONICAL_OUTER_RADIUS

const ACTIVITY_COLOR: Record<number, string> = {
  [ActivityType.Rest]: 'rgb(117, 197, 240)',
  [ActivityType.Availability]: 'rgb(255, 245, 0)',
  [ActivityType.Work]: 'rgb(231, 120, 23)',
  [ActivityType.Driving]: 'rgb(218, 37, 29)',
  [ActivityType.NoData]: 'rgb(220, 220, 220)',
}


const ACTIVITY_COLOR_GRAY: Record<number, string> = {
  [ActivityType.Rest]: 'rgb(177, 177, 177)',
  [ActivityType.Availability]: 'rgb(220, 220, 220)',
  [ActivityType.Work]: 'rgb(142, 142, 142)',
  [ActivityType.Driving]: 'rgb(90, 90, 90)',
  [ActivityType.NoData]: 'rgb(190, 190, 190)',
}

const ACTIVITY_LABEL: Record<number, string> = {
  [ActivityType.Rest]: 'Odpoczynek',
  [ActivityType.Availability]: 'Dyspozycja',
  [ActivityType.Work]: 'Praca',
  [ActivityType.Driving]: 'Jazda',
  [ActivityType.NoData]: 'Brak danych',
}

export const ZOOM_MIN = 1.0
export const ZOOM_MAX = 5.0
const WHEEL_STEP = 0.0005


const DISTANCE_RING_SNAP_TOLERANCE = 12

export function DiscCanvas({
  imageUrl,
  disc,
  activities,
  onActivitiesChange,
  hoveredActivityId = null,
  selectedActivityIds,
  onHoverActivity,
  onSelectActivity,
  onContextMenuActivity,
  startBoundaryMinute = null,
  endBoundaryMinute = null,
  noonShiftMinutes = 0,
  outOfDayGrey = true,
  onChangeStartBoundary,
  onChangeEndBoundary,
  onChangeNoonShift,
  onCursorTimeChange,
  onClickEmptyArea,
  addingMode = false,
  distanceData = null,
  showDistance = false,
  onAddDistancePoint,
  onRemoveDistancePoint,
  beginCountryCode,
  endCountryCode,
  vehicleChanges = [],
  onMoveVehicleChange,
  onContextMenuVehicleChange,
  countryChanges = [],
  width = 700,
  height = 700,
  zoom = 1,
  onZoomChange,
  compactView = false,
}: Props) {
  const [img, setImg] = React.useState<HTMLImageElement | null>(null)
  React.useEffect(() => {
    if (!imageUrl) return
    const el = new window.Image()
    el.onload = () => setImg(el)
    el.src = imageUrl
  }, [imageUrl])

 
  const [tooltipPos, setTooltipPos] = React.useState<{
    x: number
    y: number
  } | null>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)


  const cx = width / 2
  const cy = height / 2

  const outerR = Math.min(width, height) / 2 - 40

  const imageScale = compactView
    ? (outerR * IMAGE_SCALE_RATIO) / CANONICAL_OUTER_RADIUS
    : (outerR * 0.55) / CANONICAL_OUTER_RADIUS
  const imageDrawW = CANONICAL_SIZE * imageScale
  const imageDrawH = CANONICAL_SIZE * imageScale
  const imageX = cx - imageDrawW / 2
  const imageY = cy - imageDrawH / 2

  const [panX, setPanX] = React.useState(0)
  const [panY, setPanY] = React.useState(0)
  React.useEffect(() => {
    if (zoom <= 1) {
      setPanX(0)
      setPanY(0)
    }
  }, [zoom])

  const dayStartIso = disc.dayStartDate

  const chartAnchorIso = React.useMemo(() => {
    const d = parseChartIso(dayStartIso)
    return toChartIso(
      new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())),
    )
  }, [dayStartIso])

  
  const minuteActivities = React.useMemo(() => {
    const wrap = (m: number) =>
      ((m % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
    return activities
      .map((a) => ({
        type: a.type,
        startMin: wrap(isoToMinute(chartAnchorIso, a.startTime)),
        endMin: wrap(isoToMinute(chartAnchorIso, a.endTime)),
        manualEntry: a.manualEntry,
        ferryTrainCrossing: a.ferryTrainCrossing,
        crew: a.crew,
        activityId: a.activityId,
      }))
      .sort((a, b) => a.startMin - b.startMin)
  }, [activities, chartAnchorIso])

  const pointerToMinute = (sx: number, sy: number): number => {
    const localX = (sx - panX) / zoom
    const localY = (sy - panY) / zoom
    const dx = localX - cx
    const dy = localY - cy
    const angle = Math.atan2(dy, dx)
    let m = ((angle - Math.PI) * MINUTES_PER_DAY) / (2 * Math.PI)
    m = ((m % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
    return m
  }

  const handleStageWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault()
    if (!onZoomChange) return
    const next = Math.max(
      ZOOM_MIN,
      Math.min(ZOOM_MAX, zoom + WHEEL_STEP * -e.evt.deltaY),
    )
    if (next === zoom) return
  
    const stage = e.target.getStage()
    const pointer = stage?.getPointerPosition()
    if (pointer) {

      const chartX = (pointer.x - panX) / zoom
      const chartY = (pointer.y - panY) / zoom
      setPanX(pointer.x - chartX * next)
      setPanY(pointer.y - chartY * next)
    }
    onZoomChange(next)
  }

  const handleStageMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage()
    if (!stage) return
    const pos = stage.getPointerPosition()
    if (!pos) return
    onCursorTimeChange?.(Math.round(pointerToMinute(pos.x, pos.y)))
    setTooltipPos({ x: pos.x, y: pos.y })
  }
  const handleStageMouseLeave = () => {
    onCursorTimeChange?.(null)
    setTooltipPos(null)
  }


  const setStageCursor = (cursor: string) => {
    const el = wrapperRef.current?.querySelector(
      'canvas',
    ) as HTMLCanvasElement | null
    if (el) el.style.cursor = cursor
  }


  const pointerToCanonical = (
    sx: number,
    sy: number,
  ): { x: number; y: number } => {
    const localX = (sx - panX) / zoom
    const localY = (sy - panY) / zoom
    return {
      x: CANONICAL_CENTER + (localX - cx) / imageScale,
      y: CANONICAL_CENTER + (localY - cy) / imageScale,
    }
  }


  const tryAddDistancePoint = (sx: number, sy: number): boolean => {
    if (!showDistance || !onAddDistancePoint || !distanceData) return false
    const c = pointerToCanonical(sx, sy)
    const dx = c.x - distanceData.circleCenterX
    const dy = c.y - distanceData.circleCenterY
    const r = Math.hypot(dx, dy)
    if (r === 0) return false
    const dStart = Math.abs(r - distanceData.startPointsRadius)
    const dEnd = Math.abs(r - distanceData.endPointsRadius)
    const kind: 'start' | 'end' = dStart <= dEnd ? 'start' : 'end'
    const nearest = Math.min(dStart, dEnd)
    if (nearest > DISTANCE_RING_SNAP_TOLERANCE) return false
    const ring =
      kind === 'start'
        ? distanceData.startPointsRadius
        : distanceData.endPointsRadius
    onAddDistancePoint(kind, {
      x: distanceData.circleCenterX + (dx / r) * ring,
      y: distanceData.circleCenterY + (dy / r) * ring,
    })
    return true
  }

  const handleStageClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
   
    if (e.target !== e.currentTarget && e.target.className !== 'Image') return
    const stage = e.target.getStage()
    if (!stage) return
    const pos = stage.getPointerPosition()
    if (!pos) return
    if (e.evt.shiftKey && tryAddDistancePoint(pos.x, pos.y)) return
    if (!onClickEmptyArea) return
    onClickEmptyArea(Math.round(pointerToMinute(pos.x, pos.y)))
  }

  
  const handleStageContextMenu = (e: Konva.KonvaEventObject<PointerEvent>) => {
    e.evt.preventDefault()
    if (!onContextMenuActivity) return
    const stage = e.target.getStage()
    const pos = stage?.getPointerPosition()
    if (!pos) return
    onContextMenuActivity(
      null,
      { x: e.evt.clientX, y: e.evt.clientY },
      Math.round(pointerToMinute(pos.x, pos.y)),
    )
  }

  const hoveredActivity = React.useMemo(
    () => activities.find((a) => a.activityId === hoveredActivityId) ?? null,
    [activities, hoveredActivityId],
  )

  return (
    <div ref={wrapperRef} className="tw-relative" style={{ width, height }}>
      <Stage
        width={width}
        height={height}
        scaleX={zoom}
        scaleY={zoom}
        x={panX}
        y={panY}
        draggable={zoom > 1}
        onDragEnd={(e) => {
     
          if (e.target !== e.target.getStage()) return
          setPanX(e.target.x())
          setPanY(e.target.y())
        }}
        onWheel={handleStageWheel}
        onMouseMove={handleStageMouseMove}
        onMouseLeave={handleStageMouseLeave}
        onClick={handleStageClick}
        onContextMenu={handleStageContextMenu}
      >
        <Layer>
          {img && (
            <KImage
              image={img}
              x={imageX}
              y={imageY}
              width={imageDrawW}
              height={imageDrawH}
              listening={false}
            />
          )}

      
          {!showDistance &&
            minuteActivities.flatMap((a, idx) => {
         
              let span = a.endMin - a.startMin
              if (span < 0) span += MINUTES_PER_DAY
              if (span <= 0) return []
              const isHover = hoveredActivityId === a.activityId
              const isSelected = selectedActivityIds?.has(a.activityId) ?? false
              const opacity = isHover || isSelected ? 0.8 : 0.6
              const baseColor = ACTIVITY_COLOR[a.type] ?? '#cbd5e1'
              const grayColor = ACTIVITY_COLOR_GRAY[a.type] ?? '#cbd5e1'

              const onMouseEnter = () => {
                onHoverActivity?.(a.activityId, true)
                setStageCursor('pointer')
              }
              const onMouseLeave = () => {
                onHoverActivity?.(null, true)
                setStageCursor('default')
              }
              const onWedgeClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
                if (addingMode && onClickEmptyArea) {
                  const pos = e.target.getStage()?.getPointerPosition()
                  if (pos) onClickEmptyArea(Math.round(pointerToMinute(pos.x, pos.y)))
                  return
                }
                onSelectActivity?.(
                  [a.activityId],
                  e.evt.shiftKey,
                  e.evt.ctrlKey || e.evt.metaKey,
                )
              }
              const onWedgeContext = (
                e: Konva.KonvaEventObject<PointerEvent>,
              ) => {
                e.evt.preventDefault()
          
                e.cancelBubble = true
                const real = activities.find(
                  (x) => x.activityId === a.activityId,
                )
                if (!real) return
                const pos = e.target.getStage()?.getPointerPosition()
                const minute = pos
                  ? Math.round(pointerToMinute(pos.x, pos.y))
                  : 0
                onContextMenuActivity?.(
                  real,
                  { x: e.evt.clientX, y: e.evt.clientY },
                  minute,
                )
              }

     
              if (span >= MINUTES_PER_DAY) {
                return [
                  <Circle
                    key={`actfull-${idx}-${a.activityId}`}
                    x={cx}
                    y={cy}
                    radius={outerR}
                    fill={baseColor}
                    opacity={opacity}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={onWedgeClick}
                    onContextMenu={onWedgeContext}
                  />,
                ]
              }
             
              const slices = outOfDayGrey
                ? splitActivityByDayWindow(
                    a.startMin,
                    span,
                    startBoundaryMinute,
                    endBoundaryMinute,
                  )
                : [{ startMin: a.startMin, spanMin: span, inDay: true }]
              return slices.map((slc, sIdx) => {
                const startDeg = (slc.startMin / MINUTES_PER_DAY) * 360 + 180
                const sweepDeg = (slc.spanMin / MINUTES_PER_DAY) * 360
                return (
                  <Wedge
                    key={`act-${idx}-${a.activityId}-${slc.startMin}-${sIdx}`}
                    x={cx}
                    y={cy}
                    radius={outerR}
                    angle={sweepDeg}
                    rotation={startDeg}
                    fill={slc.inDay ? baseColor : grayColor}
                    opacity={opacity}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onClick={onWedgeClick}
                    onContextMenu={onWedgeContext}
                  />
                )
              })
            })}

  
          <Group listening={false}>
            {Array.from({ length: 24 }).map((_, h) => {
              const a = minuteToAngleRad(h * 60 - noonShiftMinutes)
              return (
                <Line
                  key={`spoke-${h}`}
                  points={[
                    cx + (outerR - 14) * Math.cos(a),
                    cy + (outerR - 14) * Math.sin(a),
                    cx + (outerR + 14) * Math.cos(a),
                    cy + (outerR + 14) * Math.sin(a),
                  ]}
                  stroke="#0f172a"
                  strokeWidth={h % 6 === 0 ? 2 : 1}
                />
              )
            })}
            {Array.from({ length: 24 }).map((_, h) => {
              const a = minuteToAngleRad(h * 60 - noonShiftMinutes)
              const r = outerR + 26
              return (
                <Text
                  key={`hour-${h}`}
                  text={String(h)}
                  x={cx + r * Math.cos(a) - 7}
                  y={cy + r * Math.sin(a) - 8}
                  fontSize={13}
                  fontStyle="bold"
                  fill="#0f172a"
                />
              )
            })}
            <Circle
              x={cx}
              y={cy}
              radius={outerR}
              stroke="#0f172a"
              strokeWidth={1}
            />
          </Group>

        
          {minuteActivities
            .filter(
              (a) =>
                hoveredActivityId === a.activityId ||
                selectedActivityIds?.has(a.activityId),
            )
            .map((a) => {
              const sa = minuteToAngleRad(a.startMin)
              const ea = minuteToAngleRad(a.endMin)
              return (
                <Group
                  key={`endpoints-${a.activityId}-${a.startMin}`}
                  listening={false}
                >
                  <Circle
                    x={cx + outerR * Math.cos(sa)}
                    y={cy + outerR * Math.sin(sa)}
                    radius={5}
                    fill={ACTIVITY_COLOR[a.type] ?? '#cbd5e1'}
                    stroke="#0f172a"
                    strokeWidth={1}
                  />
                  <Circle
                    x={cx + outerR * Math.cos(ea)}
                    y={cy + outerR * Math.sin(ea)}
                    radius={5}
                    fill={ACTIVITY_COLOR[a.type] ?? '#cbd5e1'}
                    stroke="#0f172a"
                    strokeWidth={1}
                  />
                </Group>
              )
            })}

      
          <ActivityBoundaryHandles
            activities={activities}
            cx={cx}
            cy={cy}
            radius={outerR}
            hoveredActivityId={hoveredActivityId}
            selectedActivityIds={selectedActivityIds}
            onActivitiesChange={onActivitiesChange}
            onHoverActivity={onHoverActivity}
            setCursor={setStageCursor}
          />

          {startBoundaryMinute != null && (
            <BoundaryMark
              cx={cx}
              cy={cy}
              handleR={outerR + 14}
              minute={startBoundaryMinute}
              color="#22c55e"
              onChange={onChangeStartBoundary}
              countryCode={beginCountryCode}
            />
          )}
          {endBoundaryMinute != null && (
            <BoundaryMark
              cx={cx}
              cy={cy}
              handleR={outerR + 14}
              minute={endBoundaryMinute}
              color="#ef4444"
              onChange={onChangeEndBoundary}
              countryCode={endCountryCode}
            />
          )}

          {onChangeNoonShift && (
            <BoundaryMark
              cx={cx}
              cy={cy}
              handleR={outerR + 14}
              minute={
                (((12 * 60 - noonShiftMinutes) % MINUTES_PER_DAY) +
                  MINUTES_PER_DAY) %
                MINUTES_PER_DAY
              }
              color="#0f172a"
              onChange={(m) => onChangeNoonShift(12 * 60 - m)}
            />
          )}

      
          {countryChanges.map((cc, idx) => {
            const a = minuteToAngleRad(cc.minute)
            const labelR = outerR + 26
            return (
              <Group key={`cc-${idx}-${cc.minute}`} listening={false}>
                <Line
                  points={[
                    cx,
                    cy,
                    cx + outerR * Math.cos(a),
                    cy + outerR * Math.sin(a),
                  ]}
                  stroke="#a855f7"
                  strokeWidth={2}
                  opacity={0.7}
                />
                <Text
                  text={cc.code}
                  x={cx + labelR * Math.cos(a) - 9}
                  y={cy + labelR * Math.sin(a) - 7}
                  fontSize={11}
                  fontStyle="bold"
                  fill="#7e22ce"
                />
              </Group>
            )
          })}

          {vehicleChanges.map((change) => (
            <VehicleChangeMark
              key={`vc-${change.id}`}
              cx={cx}
              cy={cy}
              outerR={outerR}
              minute={change.minute}
              label={change.label}
              onMove={
                onMoveVehicleChange
                  ? (minute) => onMoveVehicleChange(change.id, minute)
                  : undefined
              }
              onContextMenu={
                onContextMenuVehicleChange
                  ? (screenXY) => onContextMenuVehicleChange(change.id, screenXY)
                  : undefined
              }
            />
          ))}

          
          {showDistance && distanceData && (
            <Group>
              <Circle
                x={
                  cx +
                  (distanceData.circleCenterX - CANONICAL_CENTER) * imageScale
                }
                y={
                  cy +
                  (distanceData.circleCenterY - CANONICAL_CENTER) * imageScale
                }
                radius={distanceData.circleRadius * imageScale}
                stroke="#0ea5e9"
                strokeWidth={1.2}
                dash={[4, 3]}
                listening={false}
              />
              {distanceData.startPoints.map((p, i) => (
                <Circle
                  key={`ds-${i}`}
                  x={cx + (p.x - CANONICAL_CENTER) * imageScale}
                  y={cy + (p.y - CANONICAL_CENTER) * imageScale}
                  radius={4 * imageScale}
                  fill="#dc2626"
                  listening={!!onRemoveDistancePoint}
                  hitStrokeWidth={12}
                  onMouseEnter={() => setStageCursor('pointer')}
                  onMouseLeave={() => setStageCursor('default')}
                  onClick={(e) => {
                    e.cancelBubble = true
                    onRemoveDistancePoint?.('start', i)
                  }}
                />
              ))}
              {distanceData.endPoints.map((p, i) => (
                <Circle
                  key={`de-${i}`}
                  x={cx + (p.x - CANONICAL_CENTER) * imageScale}
                  y={cy + (p.y - CANONICAL_CENTER) * imageScale}
                  radius={4 * imageScale}
                  fill="#0ea5e9"
                  listening={!!onRemoveDistancePoint}
                  hitStrokeWidth={12}
                  onMouseEnter={() => setStageCursor('pointer')}
                  onMouseLeave={() => setStageCursor('default')}
                  onClick={(e) => {
                    e.cancelBubble = true
                    onRemoveDistancePoint?.('end', i)
                  }}
                />
              ))}
            </Group>
          )}

        </Layer>
      </Stage>
      
      {hoveredActivity && tooltipPos && (
        <div
          className="tw-pointer-events-none tw-absolute tw-z-[5] tw-rounded tw-border tw-border-slate-300 tw-bg-white tw-px-2 tw-py-1 tw-text-xs tw-shadow-md"
          style={{ left: tooltipPos.x + 14, top: tooltipPos.y + 14 }}
        >
          <div className="tw-font-semibold tw-text-slate-800">
            {ACTIVITY_LABEL[hoveredActivity.type] ?? '?'}
          </div>
          <div className="tw-font-mono tw-text-slate-600">
            {hoverTimeRange(hoveredActivity, chartAnchorIso, noonShiftMinutes)}
          </div>
        </div>
      )}
    </div>
  )
}

function hoverTimeRange(
  a: ChartScanActivity,
  dayStartIso: string,
  noonShiftMinutes: number,
): string {
  const fmt = (iso: string) => {
    const m = isoToMinute(dayStartIso, iso) + noonShiftMinutes
    const wrap = ((m % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
    const h = Math.floor(wrap / 60)
    const mm = wrap - h * 60
    return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
  }
  let ms = +new Date(a.endTime) - +new Date(a.startTime)
  if (ms < 0) ms += 24 * 60 * 60 * 1000
  const dMin = Math.round(ms / 60_000)
  const dh = Math.floor(dMin / 60)
  const dm = dMin - dh * 60
  return `${fmt(a.startTime)} – ${fmt(a.endTime)} · ${String(dh).padStart(2, '0')}:${String(dm).padStart(2, '0')}`
}


function isInsideDay(
  startMin: number,
  endMin: number,
  dayStart: number | null | undefined,
  dayEnd: number | null | undefined,
): boolean {
  if (dayStart == null || dayEnd == null) return true
  if (dayEnd >= dayStart) {
    return endMin > dayStart && startMin < dayEnd
  }
  return endMin > dayStart || startMin < dayEnd
}


type WedgeSlice = { startMin: number; spanMin: number; inDay: boolean }
function splitActivityByDayWindow(
  aStartMin: number,
  aSpanMin: number,
  dayStart: number | null | undefined,
  dayEnd: number | null | undefined,
): WedgeSlice[] {
  if (dayStart == null || dayEnd == null) {
    return [{ startMin: aStartMin, spanMin: aSpanMin, inDay: true }]
  }
  
  const M = MINUTES_PER_DAY
  const inWindow = (minute: number) => {
    const m = ((minute % M) + M) % M
    if (dayEnd >= dayStart) return m >= dayStart && m < dayEnd
    return m >= dayStart || m < dayEnd
  }
  const out: WedgeSlice[] = []
  let runStart = 0
  let runIn = inWindow(aStartMin)
  for (let i = 1; i <= aSpanMin; i++) {
    const inHere = i < aSpanMin ? inWindow(aStartMin + i) : !runIn
    if (inHere !== runIn) {
      out.push({
        startMin: (((aStartMin + runStart) % M) + M) % M,
        spanMin: i - runStart,
        inDay: runIn,
      })
      runStart = i
      runIn = inHere
    }
  }
  return out
}

function BoundaryMark({
  cx,
  cy,
  handleR,
  minute,
  color,
  onChange,
  countryCode,
}: {
  cx: number
  cy: number
  handleR: number
  minute: number
  color: string
  onChange?: (minute: number) => void
 
  countryCode?: string
}) {
  const angle = minuteToAngleRad(minute)
  const px = cx + handleR * Math.cos(angle)
  const py = cy + handleR * Math.sin(angle)

  const [hover, setHover] = React.useState(false)
  const r = hover ? 9 : 7
  return (
    <Group>
      <Line
        points={[cx, cy, px, py]}
        stroke={color}
        strokeWidth={2}
        opacity={hover ? 0.9 : 0.5}
        listening={false}
      />
      {countryCode && (
        <Text
          x={cx + (handleR + 14) * Math.cos(angle) - 9}
          y={cy + (handleR + 14) * Math.sin(angle) - 7}
          text={countryCode}
          fontSize={11}
          fontStyle="bold"
          fill={color}
          listening={false}
        />
      )}
      <Circle
        x={px}
        y={py}
        radius={r}
        fill={color}
        stroke="#ffffff"
        strokeWidth={2}
        shadowColor={color}
        shadowBlur={hover ? 8 : 0}
        shadowOpacity={hover ? 0.7 : 0}
        draggable={!!onChange}
  
        onMouseDown={(e) => {
          if (onChange) e.cancelBubble = true
        }}
        onMouseEnter={(e) => {
          setHover(true)
          const c = e.target.getStage()?.container()
          if (c) c.style.cursor = onChange ? 'grab' : 'default'
        }}
        onMouseLeave={(e) => {
          setHover(false)
          const c = e.target.getStage()?.container()
          if (c) c.style.cursor = 'default'
        }}
        onDragStart={(e) => {
          e.cancelBubble = true
          const c = e.target.getStage()?.container()
          if (c) c.style.cursor = 'grabbing'
        }}
        onDragEnd={(e) => {
          const c = e.target.getStage()?.container()
          if (c) c.style.cursor = 'default'
        }}
        onDragMove={(e) => {
          const node = e.target
          const dx = node.x() - cx
          const dy = node.y() - cy
          const a = Math.atan2(dy, dx)
          let m = ((a - Math.PI) * MINUTES_PER_DAY) / (2 * Math.PI)
          m = ((m % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
          onChange?.(Math.round(m))
          node.position({
            x: cx + handleR * Math.cos(a),
            y: cy + handleR * Math.sin(a),
          })
        }}
      />
    </Group>
  )
}

// WPF TachographChartVehicleChangeControl: a white line with a grab point. Dragging the
// point moves the change, right-click edits or removes it, hover shows vehicle and odometer.
function VehicleChangeMark({
  cx,
  cy,
  outerR,
  minute,
  label,
  onMove,
  onContextMenu,
}: {
  cx: number
  cy: number
  outerR: number
  minute: number
  label?: string
  onMove?: (minute: number) => void
  onContextMenu?: (screenXY: { x: number; y: number }) => void
}) {
  const [hover, setHover] = React.useState(false)
  const [dragMinute, setDragMinute] = React.useState<number | null>(null)
  const handleR = outerR * 0.85
  const at = (m: number, r: number) => {
    const a = minuteToAngleRad(m)
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) }
  }
  const shown = dragMinute ?? minute
  const tip = at(shown, outerR)
  const handle = at(shown, handleR)
  const handleRef = React.useRef<Konva.Circle>(null)
  // react-konva skips props equal to the last render, so a dragged node is put back by hand.
  React.useLayoutEffect(() => {
    handleRef.current?.position(handle)
  })

  const enter = (e: Konva.KonvaEventObject<MouseEvent>) => {
    setHover(true)
    const c = e.target.getStage()?.container()
    if (!c) return
    c.style.cursor = onMove ? 'grab' : 'pointer'
    c.title = label ?? ''
  }
  const leave = (e: Konva.KonvaEventObject<MouseEvent>) => {
    setHover(false)
    const c = e.target.getStage()?.container()
    if (!c) return
    c.style.cursor = 'default'
    c.title = ''
  }
  const context = (e: Konva.KonvaEventObject<PointerEvent>) => {
    e.evt.preventDefault()
    e.cancelBubble = true
    onContextMenu?.({ x: e.evt.clientX, y: e.evt.clientY })
  }
  return (
    <Group>
      <Line
        points={[cx, cy, tip.x, tip.y]}
        stroke="#ffffff"
        strokeWidth={3}
        hitStrokeWidth={10}
        shadowColor="#0f172a"
        shadowBlur={2}
        shadowOpacity={0.4}
        onMouseEnter={enter}
        onMouseLeave={leave}
        onContextMenu={context}
      />
      <Circle
        ref={handleRef}
        x={handle.x}
        y={handle.y}
        radius={hover || dragMinute != null ? 7 : 5}
        fill="#ffffff"
        stroke="#1e293b"
        strokeWidth={1.5}
        draggable={!!onMove}
        onMouseDown={(e) => {
          e.cancelBubble = true
        }}
        onMouseEnter={enter}
        onMouseLeave={leave}
        onContextMenu={context}
        onDragStart={(e) => {
          e.cancelBubble = true
          const c = e.target.getStage()?.container()
          if (c) c.style.cursor = 'grabbing'
        }}
        onDragMove={(e) => {
          const node = e.target
          const a = Math.atan2(node.y() - cy, node.x() - cx)
          let m = ((a - Math.PI) * MINUTES_PER_DAY) / (2 * Math.PI)
          m = ((m % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
          setDragMinute(Math.round(m) % MINUTES_PER_DAY)
          node.position({ x: cx + handleR * Math.cos(a), y: cy + handleR * Math.sin(a) })
        }}
        onDragEnd={(e) => {
          e.cancelBubble = true
          if (dragMinute != null && dragMinute !== minute) onMove?.(dragMinute)
          setDragMinute(null)
          const c = e.target.getStage()?.container()
          if (c) c.style.cursor = 'default'
        }}
      />
    </Group>
  )
}
